import { getContentById } from '@databases/utils/confluence/content';

import { JiraService } from '@canvas/atlassian-service/jira';
import { SiteService } from '@canvas/atlassian-service/site';

import { JiraIssueDataLoader } from '../data-loaders/jira-issue-data-loader';
import { UserDataLoader } from '../data-loaders/user-data-loader';

import type {
  ExternalDataResolver,
  PageLinkResult,
  UserResult,
} from './external-data-resolver';

/**
 * Browser implementation of ExternalDataResolver.
 *
 * Wraps the existing AIFC browser fetch utilities — cookie-based auth,
 * relative URLs, UFO trace headers — with zero behaviour change.
 * Use this in the AIFC (browser) flow.
 */
export class BrowserExternalDataResolver implements ExternalDataResolver {
  private readonly userDataLoader: UserDataLoader;
  private readonly jiraIssueDataLoaders: Map<string, JiraIssueDataLoader>;

  constructor() {
    this.userDataLoader = new UserDataLoader();
    this.jiraIssueDataLoaders = new Map();
  }

  async resolveUsers(accountIds: string[]): Promise<(UserResult | null)[]> {
    const results = await this.userDataLoader.loadMany(accountIds);
    return results.map((result) => {
      if ('accountId' in result) {
        return {
          accountId: result.accountId,
          ...(result.displayName !== undefined && { displayName: result.displayName }),
        };
      }
      return null;
    });
  }

  async resolvePageLink(contentId: string): Promise<PageLinkResult | null> {
    try {
      const content = await getContentById(contentId);
      if (!content) {
        return null;
      }
      // ari is not stored — getConfluencePageAriFromContentRef derives it at render
      // time from contentId via SiteService (browser-only, correct context).
      return { contentId: content.id };
    } catch {
      return null;
    }
  }

  async resolveJiraIssues(
    issueKeys: string[],
    cloudId: string
  ): Promise<(string | null)[]> {
    const loader = this.getOrCreateJiraDataLoader(cloudId);
    const results = await loader.loadMany(issueKeys);
    return results.map((result) => (typeof result === 'string' ? result : null));
  }

  resolveCloudId(jiraIssueUrl: string): Promise<string | undefined> {
    try {
      const urlOrigin = new URL(jiraIssueUrl);
      const jiraSite = SiteService.getInstance().getJiraSiteForOrigin(urlOrigin.origin);
      return Promise.resolve(jiraSite?.cloudId);
    } catch {
      return Promise.resolve(undefined);
    }
  }

  private getOrCreateJiraDataLoader(cloudId: string): JiraIssueDataLoader {
    let loader = this.jiraIssueDataLoaders.get(cloudId);
    if (!loader) {
      const jiraBaseUrl = new JiraService().getTenantBaseUrl({ cloudId });
      loader = new JiraIssueDataLoader();
      loader.setJiraBaseUrl(jiraBaseUrl);
      this.jiraIssueDataLoaders.set(cloudId, loader);
    }
    return loader;
  }
}
